import { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import './WelcomeScreen.css';

const BirthdayAnimation = ({ open, onAnimationComplete, birthdayData }) => {
  const [showAnimation, setShowAnimation] = useState(false);
  const [blastParticles, setBlastParticles] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {
      if (birthdayData) {
        setShowAnimation(true);
        // Generate blast particles
        generateBlastParticles();
        // No automatic timeout - let user control when to proceed
      } else {
        if (onAnimationComplete) {
          onAnimationComplete(false);
        }
      }
    }
  }, [open, birthdayData, onAnimationComplete]);

  const generateBlastParticles = () => {
    const particles = [];
    const colors = ['#FF6B9D', '#4ECDC4', '#45B7D1', '#FFA726', '#66BB6A', '#AB47BC', '#FF7043', '#26C6DA'];
    const shapes = ['●', '★', '♦', '♥', '♠', '▲', '■', '◆'];
    
    // Top blast particles
    for (let i = 0; i < 30; i++) {
      particles.push({
        id: `top-${i}`,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 3 + Math.random() * 2,
        size: 0.8 + Math.random() * 1.2,
        type: 'top'
      });
    }
    
    // Bottom blast particles
    for (let i = 0; i < 30; i++) {
      particles.push({
        id: `bottom-${i}`,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 3 + Math.random() * 2,
        size: 0.8 + Math.random() * 1.2,
        type: 'bottom'
      });
    }
    
    setBlastParticles(particles);
  };



  if (!open || !showAnimation || !birthdayData) {
    return null;
  }

  const handleBirthdayClick = () => {
    setShowAnimation(false);
    if (onAnimationComplete) {
      onAnimationComplete(true, birthdayData);
    }
  };

  return (
    <Dialog
      open={showAnimation}
      maxWidth={false}
      fullScreen
      onClick={handleBirthdayClick}
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'hidden',
          cursor: 'pointer',
        },
      }}
    >
      <DialogContent
        style={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: isMobile ? '100dvh' : '100vh',
          position: 'relative',
          background: `
            linear-gradient(135deg, rgba(255, 229, 241, 0.8) 0%, rgba(232, 245, 232, 0.8) 25%, rgba(227, 242, 253, 0.8) 50%, rgba(255, 243, 224, 0.8) 75%, rgba(243, 229, 245, 0.8) 100%),
            radial-gradient(circle at 20% 30%, rgba(255, 107, 157, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(78, 205, 196, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255, 167, 38, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 70% 20%, rgba(171, 71, 188, 0.15) 0%, transparent 50%)
          `,
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          overflow: 'hidden',
        }}
      >
        {/* Top Blast Particles */}
        <Box className="top-blast-container">
          {blastParticles
            .filter(p => p.type === 'top')
            .map((particle) => (
              <Box
                key={particle.id}
                className="blast-particle top-particle"
                sx={{
                  position: 'absolute',
                  top: '-10%',
                  left: `${particle.left}%`,
                  color: particle.color,
                  fontSize: `${particle.size}rem`,
                  animationDelay: `${particle.delay}s`,
                  animationDuration: `${particle.duration}s`,
                  zIndex: 3,
                  filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))'
                }}
              >
                {particle.shape}
              </Box>
            ))}
        </Box>
        
        {/* Bottom Blast Particles */}
        <Box className="bottom-blast-container">
          {blastParticles
            .filter(p => p.type === 'bottom')
            .map((particle) => (
              <Box
                key={particle.id}
                className="blast-particle bottom-particle"
                sx={{
                  position: 'absolute',
                  bottom: '-10%',
                  left: `${particle.left}%`,
                  color: particle.color,
                  fontSize: `${particle.size}rem`,
                  animationDelay: `${particle.delay}s`,
                  animationDuration: `${particle.duration}s`,
                  zIndex: 3,
                  filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))'
                }}
              >
                {particle.shape}
              </Box>
            ))}
        </Box>
        
        {/* Animated Background Patterns with Glassomorphism */}
        <Box
          className="birthday-background-pattern glass-background"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.15,
            background: `
              radial-gradient(circle at 20% 30%, rgba(255, 107, 157, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(78, 205, 196, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(255, 167, 38, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 70% 20%, rgba(171, 71, 188, 0.3) 0%, transparent 50%)
            `,
            backdropFilter: 'blur(10px) saturate(150%)',
            WebkitBackdropFilter: 'blur(10px) saturate(150%)',
            zIndex: 1,
            animation: 'backgroundPulse 3s ease-in-out infinite alternate, glassBackgroundShift 8s ease-in-out infinite'
          }}
        />

        {/* Birthday Message */}
        <Box
          className="birthday-message-container glass-morphism-glow"
          sx={{
            position: 'relative',
            zIndex: 4,
            textAlign: 'center',
            padding: isMobile ? 3 : 4,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(30px) saturate(200%)',
            WebkitBackdropFilter: 'blur(30px) saturate(200%)',
            maxWidth: isMobile ? '85%' : '65%',
            margin: '0 auto',
            boxShadow: `
              0 8px 32px rgba(31, 38, 135, 0.37),
              inset 0 1px 0 rgba(255, 255, 255, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.25)
            `,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            animation: 'birthdayMessageEntrance 1s ease-out, glassGlow 3s ease-in-out infinite'
          }}
        >
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h1"
            sx={{
              fontWeight: '800',
              marginBottom: 3,
              background: 'linear-gradient(135deg, #FF6B9D, #4ECDC4, #FFA726, #AB47BC)',
              backgroundSize: '400% 400%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'birthdayTitleGlow 2s ease-in-out infinite, gradientShift 3s ease infinite',
              fontSize: isMobile ? '2.2rem' : '3.5rem',
              fontFamily: '"Comic Neue", "Fredoka One", cursive, sans-serif',
              letterSpacing: '2px',
              textAlign: 'center',
              lineHeight: 1.2
            }}
          >
            🎉 Happy Birthday! 🎂
          </Typography>
          
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{
              marginBottom: 2,
              color: '#2D3748',
              fontWeight: '700',
              fontSize: isMobile ? '1.4rem' : '1.8rem',
              fontFamily: '"Poppins", "Roboto", sans-serif',
              animation: 'slideInUp 1s ease-out 0.3s both',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            {birthdayData?.user_name || 'Dear Student'}
          </Typography>
          
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            sx={{
              color: '#4A5568',
              fontSize: isMobile ? '1rem' : '1.2rem',
              fontFamily: '"Inter", "Roboto", sans-serif',
              fontWeight: '500',
              lineHeight: 1.6,
              animation: 'fadeInUp 1s ease-out 0.6s both',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
          >
            Wishing you a wonderful day filled with happiness and joy! 🎈
          </Typography>
        </Box>

        {/* Enhanced Floating Balloons */}
        <Box className="enhanced-balloons-container">
          <div className="enhanced-balloon balloon-1" style={{color: '#FF6B9D'}}>🎈</div>
          <div className="enhanced-balloon balloon-2" style={{color: '#4ECDC4'}}>🎈</div>
          <div className="enhanced-balloon balloon-3" style={{color: '#FFA726'}}>🎈</div>
          <div className="enhanced-balloon balloon-4" style={{color: '#66BB6A'}}>🎈</div>
          <div className="enhanced-balloon balloon-5" style={{color: '#AB47BC'}}>🎈</div>
          <div className="enhanced-balloon balloon-6" style={{color: '#FF7043'}}>🎈</div>
        </Box>

        {/* Enhanced Confetti */}
        <Box className="enhanced-confetti-container">
          <div className="enhanced-confetti confetti-1" style={{color: '#FF6B9D'}}>🎊</div>
          <div className="enhanced-confetti confetti-2" style={{color: '#4ECDC4'}}>🎉</div>
          <div className="enhanced-confetti confetti-3" style={{color: '#FFA726'}}>🎊</div>
          <div className="enhanced-confetti confetti-4" style={{color: '#66BB6A'}}>🎉</div>
          <div className="enhanced-confetti confetti-5" style={{color: '#AB47BC'}}>🎊</div>
          <div className="enhanced-confetti confetti-6" style={{color: '#FF7043'}}>🎉</div>
          <div className="enhanced-confetti confetti-7" style={{color: '#26C6DA'}}>✨</div>
          <div className="enhanced-confetti confetti-8" style={{color: '#EC407A'}}>⭐</div>
        </Box>
        
        {/* Sparkle Effects */}
        <Box className="sparkle-effects">
          <div className="sparkle sparkle-1">✨</div>
          <div className="sparkle sparkle-2">⭐</div>
          <div className="sparkle sparkle-3">✨</div>
          <div className="sparkle sparkle-4">⭐</div>
          <div className="sparkle sparkle-5">💫</div>
          <div className="sparkle sparkle-6">🌟</div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BirthdayAnimation;